import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, HeartPulse, Sparkles } from "lucide-react";
import Seo from "../components/Seo";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { IMAGES } from "../lib/data";

const values = [
  { icon: Target, title: "Evidence-based", text: "Every treatment is grounded in the latest research and clinical reasoning, with no guesswork and no gimmicks." },
  { icon: HeartPulse, title: "Movement-first", text: "We believe movement is medicine. Hands-on care is paired with progressive exercise to create lasting change." },
  { icon: Sparkles, title: "Personalised", text: "Your plan is built around your body, your goals and your life, never a one-size-fits-all program." },
];

export default function About() {
  return (
    <>
      <Seo title="About Us" description="Learn about Arena Movement Physiotherapy: evidence-based, movement-first physiotherapy for active people in Melbourne." path="/about" />
      <PageHeader
        eyebrow="About Us"
        title="Care built around movement, evidence and you"
        subtitle="Arena Movement Physiotherapy helps people recover from injury, reduce pain and keep doing what they enjoy."
      />

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="overflow-hidden rounded-[2rem] group">
              <img src="https://static.prod-images.emergentagent.com/jobs/18fb342f-6b84-42a9-b260-9a0eb5b58246/images/4707681ed1fe08b720c908275934a9511a09206905212a5d23297a47533d7079.png" alt="Physiotherapist guiding a client through movement-based rehabilitation" className="w-full h-[480px] object-cover img-zoom" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-text-dark tracking-tight">Our story</h2>
              <div className="mt-5 space-y-4 text-lg text-text-muted leading-relaxed">
                <p>
                  Arena Movement Physiotherapy provides evidence-based physiotherapy, rehabilitation and
                  movement-based care to help people recover from injury, reduce pain, and continue doing what they enjoy.
                </p>
                <p>
                  Based within performance and training environments, we work with active individuals of all ages,
                  from gym members and recreational athletes to those looking to improve strength, mobility and
                  confidence in movement.
                </p>
                <p>
                  We combine thorough hands-on assessment with progressive exercise rehabilitation, always taking the
                  time to explain what's going on and why. Our goal is simple: get you moving better and keep you there.
                </p>
              </div>
              <Link to="/book" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 font-semibold text-white transition-all hover:-translate-y-0.5" data-testid="about-book-button">
                Book an appointment <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 sm:py-28 bg-offwhite">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-text-dark tracking-tight max-w-xl">What we stand for</h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <div className="h-full bg-white rounded-3xl border border-border-light p-8 card-hover">
                  <div className="h-12 w-12 grid place-items-center rounded-2xl bg-navy text-white"><v.icon className="h-6 w-6" /></div>
                  <h3 className="mt-5 font-display text-xl font-semibold text-text-dark">{v.title}</h3>
                  <p className="mt-2 text-text-muted leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
